import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {DefaultEventsMap, EventDataType, IEventManagerWithAsync, IView, ListenerType} from '@gongxigames/framework/core/types';

export interface ModuleStateConstructor<
    V extends IView<EventsMap> | null,
    M extends Module<V, EventsMap>,
    EventsMap = DefaultEventsMap,
    S extends ModuleState<V, M, EventsMap> = ModuleState<V, M, EventsMap>
> {
    new (module: M, name: string): S;
}

/**
 * module state handles events
 */
export abstract class ModuleState<V extends IView<EventsMap> | null, M extends Module<V, EventsMap>, EventsMap = DefaultEventsMap> {
    protected _name: string;

    protected _module: M;

    /**
     * the list of events we handle with this state
     */
    protected _events: Record<any, Function> = {};

    protected _eventManager: IEventManagerWithAsync<EventsMap>;

    public abstract onEnterState(): void;

    public abstract onLeaveState(): void;

    protected abstract setupEvents(): void;

    constructor(module: M, name: string) {
        this._module = module;
        this._name = name;
        this._eventManager = module.eventManager;

        this.setupEvents();
    }

    protected emit<K extends keyof EventsMap>(id: K, ...args: EventDataType<EventsMap[K]>): void {
        this._eventManager.emit(id, ...args);
    }

    protected emitDelayed<K extends keyof EventsMap>(id: K, ...args: EventDataType<EventsMap[K]>): void {
        this._module.updateManager.addTimer(() => {
            this.emit(id, ...args);
        }, 0, true);
    }

    /**
     * handles events
     * @param {number} id - event ID
     * @param args
     */
    public handle<K extends keyof EventsMap>(id: K, ...args: EventDataType<EventsMap[K]>): void {
        this._events[id] && this._events[id](...args);
    }

    public setEventHandler<K extends keyof EventsMap>(id: K, callback: ListenerType<EventsMap[K]>): void {
        this._events[id] = callback;
    }

    protected get module(): M {
        return this._module;
    }

    protected get view(): V {
        return this._module.view;
    }

    public get name(): string {
        return this._name;
    }
}
